import React, { useCallback } from "react"

import { Attendee, AudioVideoFacade } from "amazon-chime-sdk-js"

import { findGeminiLanguage } from "src/geminiLanguageOptions"

import useScrollToBottom from "hooks/useScrollToBottom"

import useChimeCaptions from "components/ChimeMeetingHooks/useChimeCaptions"

import FormattedCaptionText from "./FormattedCaptionText"
import { RosterEntry } from "./useRoster"

const captionForChunk = (
  chunk: {
    attendeeId?: string
    text: string
    vocabularyFilterMatches: string[]
  },
  roster: Record<string, RosterEntry>
) => {
  const speaker = chunk.attendeeId ? roster[chunk.attendeeId] : undefined
  const languageCode = speaker?.defaultLiveTranslationLanguageCode
  if (speaker?.userType !== "Student" || !languageCode) {
    return {
      text: chunk.text,
      vocabularyFilterMatches: chunk.vocabularyFilterMatches,
    }
  }

  const languageName = findGeminiLanguage(languageCode)?.label || languageCode
  return {
    text: `<student is speaking in ${languageName}>`,
    vocabularyFilterMatches: [],
  }
}

export type TranscriptChunkHistory = {
  id: string
  speakerName: string
  text: string
  vocabularyFilterMatches: string[]
}

type FormattedCaptionChunk = {
  key: string
  speakerLabel: string
  text: string
  vocabularyFilterMatches: string[]
}

type CaptionViewProps = {
  audioVideo: AudioVideoFacade | undefined
  highlightedWords?: string[]
  roster: Record<string, RosterEntry>
  historyChunks?: TranscriptChunkHistory[]
}

const CaptionView: React.FC<CaptionViewProps> = ({
  audioVideo,
  highlightedWords = [],
  roster,
  historyChunks,
}) => {
  const buildSpeakerLabel = useCallback(
    (attendee?: Attendee) =>
      (attendee?.attendeeId && roster[attendee.attendeeId]?.fullName) ||
      "Participant",
    [roster]
  )

  const { captionChunks } = useChimeCaptions({
    audioVideo,
    buildSpeakerLabel,
  })
  const formattedHistoryChunks: FormattedCaptionChunk[] = (
    historyChunks || []
  ).map(chunk => ({
    key: `history-${chunk.id}`,
    speakerLabel: chunk.speakerName,
    text: chunk.text,
    vocabularyFilterMatches: chunk.vocabularyFilterMatches,
  }))
  const formattedCaptionChunks: FormattedCaptionChunk[] = captionChunks.map(
    chunk => ({
      key: `caption-${chunk.resultId}`,
      speakerLabel: chunk.speakerLabel,
      ...captionForChunk(chunk, roster),
    })
  )
  const formattedChunks = [...formattedHistoryChunks, ...formattedCaptionChunks]
  const scrollRef = useScrollToBottom<HTMLDivElement>(
    captionChunks,
    historyChunks
  )

  return (
    <div
      ref={scrollRef}
      className="h-full overflow-y-auto p-6 pb-24 text-left text-lg leading-relaxed text-white/95"
      role="log"
      aria-live="polite"
    >
      {formattedChunks.length === 0 ? (
        <span className="text-white/50">Captions will appear here</span>
      ) : (
        formattedChunks.map(chunk => (
          <p key={chunk.key} className="mb-2 last:mb-0">
            <span className="font-semibold text-white">
              {chunk.speakerLabel}
              {": "}
            </span>
            <span>
              <FormattedCaptionText
                text={chunk.text}
                redactedWords={chunk.vocabularyFilterMatches}
                highlightedWords={highlightedWords}
              />
            </span>
          </p>
        ))
      )}
    </div>
  )
}

export default CaptionView
